import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import { useCreateOrganisationSampleSheet, useUpdateLabWorkbookMetadata } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { getAuthHeaders, resistomapApiClient } from '@resistapp/client/utils/fetch';
import {
  type AdminGenePanel,
  type AdminGenePanelAssaysResponse,
  type AdminGenePanelPatchRequest,
  type AdminGenePanelPatchResponse,
  type AdminGenePanelsResponse,
  type CreateSampleSheetRequest,
  type LabWorkbookDeleteEligibility,
  type LabWorkbookResetEligibility,
} from '@resistapp/common/api-types';
import {
  DefaultGenePanelDefinition,
  DefaultGenePanelDefinitionError,
  buildDefaultGenePanelDefinitions,
  defaultGenePanelKeys,
  genePanelConfigurationIds,
  getGenePanelConfiguration,
  isGenePanelConfigurationId,
  type DefaultGenePanelKey,
  type GenePanelConfigurationId,
} from '@resistapp/common/gene-panels';
import {
  labWorkbookServiceChoices,
  normalizeLabWorkbookServices,
  type LabWorkbookService,
} from '@resistapp/common/lab-dna-step';
import { GenePanelState } from '@resistapp/common/types';
import { normaliseSheetUrl } from '@resistapp/common/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent, FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DeleteLabWorkbookControl } from './delete-lab-workbook-control';
import { LabWorkbookShippingSettingsControl } from './lab-workbook-shipping-settings-control';
import { ResetLabWorkbookControl } from './reset-lab-workbook-control';

const CUSTOM_PANEL_BASE_ASSAYS = ['AY1'];
const LAB_SHEET_TEMPLATE_SELECTION_VALUE = '__lab_sheet_template__';
const LAB_SHEET_TEMPLATE_OPTION: { value: string; label: string } = {
  value: LAB_SHEET_TEMPLATE_SELECTION_VALUE,
  label: 'Import from legacy lab sheet (auto-detected)',
};

type CommonProps = {
  isOpen: boolean;
  onClose: () => void;
};

type CreateModeProps = CommonProps & {
  mode: 'create';
  organisationId: number;
  canAssignShippingQuota?: boolean;
  onCreated?: () => void;
};

type EditModeProps = CommonProps & {
  mode: 'edit';
  workbookId: string;
  organisationId: number;
  canManageShipping: boolean;
  initialMetadata: {
    name: string | null;
    projectLead: string | null;
    channelUrl: string | null;
  };
  resetEligibility: LabWorkbookResetEligibility;
  deleteEligibility: LabWorkbookDeleteEligibility;
  onReset?: () => void;
  onDeleted?: () => void;
};

export type LabWorkbookAdminModalProps = CreateModeProps | EditModeProps;

type LabWorkbookMetadataFormState = {
  name: string;
  projectLead: string;
  channelUrl: string;
};

type CreateLabWorkbookFormState = LabWorkbookMetadataFormState & {
  labSheetUrl: string;
  servicesChoice: string;
  shippingRequestQuota: string;
  xlShipmentsAuthorised: boolean;
};

const createEmptyMetadataFormState = (): LabWorkbookMetadataFormState => ({
  name: '',
  projectLead: '',
  channelUrl: '',
});

const createEmptyCreateFormState = (): CreateLabWorkbookFormState => ({
  ...createEmptyMetadataFormState(),
  labSheetUrl: '',
  servicesChoice: 'dna-qc',
  shippingRequestQuota: '0',
  xlShipmentsAuthorised: false,
});

type GeneTemplateFormState = {
  selection: string;
  customName: string;
};

const createEmptyGeneTemplateFormState = (): GeneTemplateFormState => ({
  selection: '',
  customName: '',
});

const normalizeLabWorkbookInputValue = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

type LabSheetUrlNormalizationResult = {
  value: string | null;
  isValid: boolean;
};

const normalizeLabSheetUrlInputValue = (value: string): LabSheetUrlNormalizationResult => {
  const normalized = normalizeLabWorkbookInputValue(value);
  if (!normalized) {
    return { value: null, isValid: true };
  }
  const canonical = normaliseSheetUrl(normalized);
  return { value: canonical ?? null, isValid: Boolean(canonical) };
};

type ChannelUrlNormalizationResult = {
  value: string | null;
  error: string | null;
};

function isSlackJoinHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();
  return normalized === 'join.slack.com' || normalized.endsWith('.join.slack.com');
}

const normalizeChannelUrlInputValue = (value: string): ChannelUrlNormalizationResult => {
  const normalized = normalizeLabWorkbookInputValue(value);
  if (!normalized) {
    return { value: null, error: null };
  }

  const parsed = (() => {
    try {
      return new URL(normalized);
    } catch {
      return null;
    }
  })();
  if (!parsed) {
    return { value: null, error: 'Enter a valid URL.' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { value: null, error: 'Enter a valid URL.' };
  }

  if (isSlackJoinHostname(parsed.hostname)) {
    return {
      value: null,
      error: 'Slack invite links (join.slack.com) are not allowed. Paste the channel URL instead (... menu > Copy link).',
    };
  }

  return { value: normalized, error: null };
};

type ParsedTemplateSelection =
  | { type: 'existing'; panelId: number }
  | { type: 'default'; key: DefaultGenePanelKey }
  | { type: 'custom'; configurationId: GenePanelConfigurationId }
  | null;

const defaultGenePanelKeySet = new Set<string>(defaultGenePanelKeys);

function isDefaultGenePanelKey(value: string): value is DefaultGenePanelKey {
  return defaultGenePanelKeySet.has(value);
}

function parseTemplateSelection(value: string): ParsedTemplateSelection {
  if (!value) {
    return null;
  }
  if (value.startsWith('existing:')) {
    const id = Number(value.slice('existing:'.length));
    return Number.isFinite(id) ? { type: 'existing', panelId: id } : null;
  }
  if (value.startsWith('default:')) {
    const keyCandidate = value.slice('default:'.length);
    return isDefaultGenePanelKey(keyCandidate) ? { type: 'default', key: keyCandidate } : null;
  }
  if (value.startsWith('custom:')) {
    const payload = value.slice('custom:'.length);
    if (isGenePanelConfigurationId(payload)) {
      return { type: 'custom', configurationId: payload };
    }
  }
  return null;
}

function getServicesForChoiceValue(value: string): LabWorkbookService[] | null {
  const choice = labWorkbookServiceChoices.find(candidate => candidate.value === value);
  return normalizeLabWorkbookServices(choice?.services);
}

function formatGenePanelStateLabel(state: GenePanelState): string {
  switch (state) {
    case GenePanelState.VALID:
      return 'Valid';
    case GenePanelState.INVALID:
      return 'Invalid';
    case GenePanelState.LOCKED:
      return 'Locked';
    default:
      return state;
  }
}

function sortPanelsByCreatedAt(left: AdminGenePanel, right: AdminGenePanel): number {
  const leftTime = new Date(left.createdAt).getTime();
  const rightTime = new Date(right.createdAt).getTime();
  return leftTime - rightTime;
}

function deriveMetadataFormStateFromProps(metadata: EditModeProps['initialMetadata']): LabWorkbookMetadataFormState {
  return {
    name: metadata.name ?? '',
    projectLead: metadata.projectLead ?? '',
    channelUrl: metadata.channelUrl ?? '',
  };
}

export function LabWorkbookAdminModal(props: LabWorkbookAdminModalProps) {
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const toast = useToast();
  const createWorkbook = useCreateOrganisationSampleSheet();
  const updateMetadata = useUpdateLabWorkbookMetadata();

  const editInitialMetadata = props.mode === 'edit' ? props.initialMetadata : null;

  const [metadataFormState, setMetadataFormState] = useState<LabWorkbookMetadataFormState>(() =>
    createEmptyMetadataFormState(),
  );
  const [createFormState, setCreateFormState] = useState<CreateLabWorkbookFormState>(() =>
    createEmptyCreateFormState(),
  );
  const [geneTemplateForm, setGeneTemplateForm] = useState<GeneTemplateFormState>(() =>
    createEmptyGeneTemplateFormState(),
  );

  const [templateSelectionError, setTemplateSelectionError] = useState<string | null>(null);
  const [customTemplateNameError, setCustomTemplateNameError] = useState<string | null>(null);
  const [channelUrlError, setChannelUrlError] = useState<string | null>(null);
  const [labSheetUrlError, setLabSheetUrlError] = useState<string | null>(null);
  const [shippingRequestQuotaError, setShippingRequestQuotaError] = useState<string | null>(null);

  const [isSubmittingWorkbook, setIsSubmittingWorkbook] = useState(false);

  const usesLabSheetTemplate = createFormState.labSheetUrl.trim().length > 0;
  useEffect(() => {
    if (usesLabSheetTemplate) {
      setTemplateSelectionError(null);
      setCustomTemplateNameError(null);
    }
  }, [usesLabSheetTemplate]);

  const organisationId = props.mode === 'create' ? props.organisationId : null;
  const genePanelEndpoint = useMemo(
    () => (typeof organisationId === 'number' ? `admin/organisation-panels/${organisationId}` : null),
    [organisationId],
  );

  const genePanelQueryEnabled =
    props.isOpen && props.mode === 'create' && typeof organisationId === 'number' && Boolean(genePanelEndpoint);
  const {
    data: genePanelsData,
    isLoading: isGenePanelsLoading,
    error: genePanelsError,
    refetch: refetchGenePanels,
  } = useQuery<AdminGenePanelsResponse>({
    queryKey: genePanelEndpoint ? [genePanelEndpoint] : ['admin/organisation-panels', 'disabled'],
    enabled: genePanelQueryEnabled,
    queryFn: async () => {
      if (!genePanelEndpoint) {
        throw new Error('Missing gene panel endpoint');
      }
      const response = await resistomapApiClient.get<AdminGenePanelsResponse>(genePanelEndpoint, {
        headers: getAuthHeaders('application/json'),
      });
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const {
    data: genePanelAssaysData,
    isLoading: isAssaysLoading,
    error: genePanelAssaysError,
    refetch: refetchGenePanelAssays,
  } = useQuery<AdminGenePanelAssaysResponse>({
    queryKey: ['admin/gene-panels/assays'],
    enabled: props.isOpen && props.mode === 'create',
    queryFn: async () => {
      const response = await resistomapApiClient.get<AdminGenePanelAssaysResponse>('admin/gene-panels/assays', {
        headers: getAuthHeaders('application/json'),
      });
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  const defaultGenePanelDefinitionsResult = useMemo(() => {
    const assays = genePanelAssaysData?.assays;
    if (!assays) {
      return { definitions: [] as DefaultGenePanelDefinition[], error: null as Error | null };
    }
    try {
      return { definitions: buildDefaultGenePanelDefinitions(assays), error: null };
    } catch (definitionError) {
      if (definitionError instanceof DefaultGenePanelDefinitionError) {
        return {
          definitions: [],
          error: new Error(
            `${definitionError.meta.name} expected ${definitionError.expectedAssays} assays but got ${definitionError.actualAssays}.`,
          ),
        };
      }
      return {
        definitions: [],
        error: definitionError instanceof Error ? definitionError : new Error('Failed to build default gene panels'),
      };
    }
  }, [genePanelAssaysData?.assays]);

  const defaultGenePanelDefinitions = defaultGenePanelDefinitionsResult.definitions;
  const defaultDefinitionError = defaultGenePanelDefinitionsResult.error;

  const panels: AdminGenePanel[] = useMemo(() => genePanelsData?.panels ?? [], [genePanelsData?.panels]);
  const panelsById = useMemo(() => new Map(panels.map(panel => [panel.id, panel])), [panels]);

  const selectionValue = geneTemplateForm.selection;
  const parsedSelection = useMemo(() => parseTemplateSelection(selectionValue), [selectionValue]);

  const selectedExistingPanel = useMemo(
    () => (parsedSelection?.type === 'existing' ? panelsById.get(parsedSelection.panelId) ?? null : null),
    [parsedSelection, panelsById],
  );

  const selectedDefaultDefinition = useMemo(
    () =>
      parsedSelection?.type === 'default'
        ? defaultGenePanelDefinitions.find(def => def.key === parsedSelection.key) ?? null
        : null,
    [parsedSelection, defaultGenePanelDefinitions],
  );

  const selectedCustomConfiguration =
    parsedSelection?.type === 'custom' ? getGenePanelConfiguration(parsedSelection.configurationId) : null;

  const isCustomSelection = !usesLabSheetTemplate && parsedSelection?.type === 'custom';

  const selectionOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a template option' }];
    if (panels.length > 0) {
      options.push(
        ...panels.map(panel => ({
          value: `existing:${panel.id}`,
          label: `Use ${panel.name} (${formatGenePanelStateLabel(panel.state)})`,
        })),
      );
    }
    if (defaultGenePanelDefinitions.length > 0) {
      options.push(
        ...defaultGenePanelDefinitions.map(def => ({
          value: `default:${def.key}`,
          label: `Commission ${def.name}`,
        })),
      );
    }
    options.push(
      ...genePanelConfigurationIds.map(configurationId => {
        const cfg = getGenePanelConfiguration(configurationId);
        return {
          value: `custom:${configurationId}`,
          label: `Create custom ${cfg.genes} genes / ${cfg.samples} samples`,
        };
      }),
    );
    return options;
  }, [defaultGenePanelDefinitions, panels]);

  const selectionInputValue = usesLabSheetTemplate ? LAB_SHEET_TEMPLATE_SELECTION_VALUE : selectionValue;
  const templateSelectOptions = usesLabSheetTemplate ? [LAB_SHEET_TEMPLATE_OPTION] : selectionOptions;

  const templateSummaryMessage = useMemo(() => {
    if (usesLabSheetTemplate) {
      return 'Assay template will be imported from the provided legacy lab sheet.';
    }
    if (selectedExistingPanel) {
      return `This workbook will use ${selectedExistingPanel.name} (${formatGenePanelStateLabel(selectedExistingPanel.state)}).`;
    }
    if (selectedDefaultDefinition) {
      const cfg = getGenePanelConfiguration(selectedDefaultDefinition.configurationId);
      return `A new ${selectedDefaultDefinition.name} (${cfg.genes} genes / ${cfg.samples} samples) template will be commissioned.`;
    }
    if (selectedCustomConfiguration) {
      return `Creates a new custom template (${selectedCustomConfiguration.genes} genes / ${selectedCustomConfiguration.samples} samples). You can finalize the gene list later.`;
    }
    return null;
  }, [selectedCustomConfiguration, selectedDefaultDefinition, selectedExistingPanel, usesLabSheetTemplate]);

  const genePanelErrorMessage = genePanelsError instanceof Error ? genePanelsError.message : null;
  const genePanelAssaysErrorMessage = genePanelAssaysError instanceof Error ? genePanelAssaysError.message : null;

  const isTemplateLoading = props.isOpen && props.mode === 'create' && (isGenePanelsLoading || isAssaysLoading);
  const isTemplateSelectionDisabled =
    usesLabSheetTemplate || isTemplateLoading || isSubmittingWorkbook || createWorkbook.isPending;

  const updatePanelCache = useCallback(
    (panel: AdminGenePanel) => {
      if (!genePanelEndpoint) {
        return;
      }
      queryClient.setQueryData<AdminGenePanelsResponse | undefined>([genePanelEndpoint], previous => {
        if (!previous) {
          return previous;
        }
        const index = previous.panels.findIndex(existingPanel => existingPanel.id === panel.id);
        const nextPanels =
          index >= 0
            ? previous.panels.map(existingPanel => (existingPanel.id === panel.id ? panel : existingPanel))
            : [...previous.panels, panel].sort((left, right) => sortPanelsByCreatedAt(left, right));
        return { ...previous, panels: nextPanels };
      });
    },
    [genePanelEndpoint, queryClient],
  );

  const commissionPanel = useCallback(
    async (payload: AdminGenePanelPatchRequest) => {
      if (!genePanelEndpoint) {
        throw new Error('Gene panel endpoint missing');
      }
      const response = await resistomapApiClient.patch<AdminGenePanelPatchResponse>(genePanelEndpoint, payload, {
        headers: getAuthHeaders('application/json'),
      });
      const panel = response.data;
      updatePanelCache(panel);
      return panel;
    },
    [genePanelEndpoint, updatePanelCache],
  );

  const ensurePanelForSelection = useCallback(
    async (selection: ParsedTemplateSelection | null, customName?: string): Promise<number> => {
      if (!selection) {
        throw new Error('Select an assay template option.');
      }
      if (selection.type === 'existing') {
        return selection.panelId;
      }
      if (selection.type === 'default') {
        const definition = defaultGenePanelDefinitions.find(def => def.key === selection.key);
        if (!definition) {
          throw new Error('Selected default template is unavailable.');
        }
        const commissionedPanel = await commissionPanel({
          name: definition.name,
          configurationId: definition.configurationId,
          assays: Array.from(definition.assays),
        });
        return commissionedPanel.id;
      }
      if (!customName) {
        throw new Error('Custom template name is required.');
      }
      const customPanel = await commissionPanel({
        name: customName,
        configurationId: selection.configurationId,
        assays: CUSTOM_PANEL_BASE_ASSAYS,
      });
      return customPanel.id;
    },
    [commissionPanel, defaultGenePanelDefinitions],
  );

  const validateCustomTemplateName = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setCustomTemplateNameError('Name is required.');
        return { valid: false, trimmed };
      }
      const normalized = trimmed.toLowerCase();
      const conflict = panels.some(panel => panel.name.trim().toLowerCase() === normalized);
      if (conflict) {
        setCustomTemplateNameError('A panel with this name already exists.');
        return { valid: false, trimmed };
      }
      setCustomTemplateNameError(null);
      return { valid: true, trimmed };
    },
    [panels],
  );

  const handleCustomTemplateNameBlur = useCallback(() => {
    if (isCustomSelection) {
      void validateCustomTemplateName(geneTemplateForm.customName);
    }
  }, [geneTemplateForm.customName, isCustomSelection, validateCustomTemplateName]);

  const handleCloseModal = useCallback(() => {
    if (isSubmittingWorkbook || createWorkbook.isPending || updateMetadata.isPending) {
      return;
    }
    props.onClose();
  }, [createWorkbook.isPending, isSubmittingWorkbook, props, updateMetadata.isPending]);

  const createModeMetadataState = createFormState;
  const editModeMetadataState = metadataFormState;
  const activeMetadataState = props.mode === 'create' ? createModeMetadataState : editModeMetadataState;

  const updateMetadataField = useCallback(
    (field: keyof LabWorkbookMetadataFormState) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (props.mode === 'create') {
        setCreateFormState(prev => ({ ...prev, [field]: value }));
      } else {
        setMetadataFormState(prev => ({ ...prev, [field]: value }));
      }
    },
    [props.mode],
  );

  const handleChannelUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (props.mode === 'create') {
        setCreateFormState(prev => ({ ...prev, channelUrl: value }));
      } else {
        setMetadataFormState(prev => ({ ...prev, channelUrl: value }));
      }
      setChannelUrlError(null);
    },
    [props.mode],
  );

  const handleCreateLabSheetUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setCreateFormState(prev => ({ ...prev, labSheetUrl: value }));
      setLabSheetUrlError(null);
    },
    [setLabSheetUrlError],
  );

  const handleCreateServicesChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setCreateFormState(prev => ({ ...prev, servicesChoice: event.target.value }));
  }, []);

  const handleShippingRequestQuotaChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCreateFormState(prev => ({ ...prev, shippingRequestQuota: event.target.value }));
    setShippingRequestQuotaError(null);
  }, []);

  const handleXlShipmentsAuthorisedChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCreateFormState(prev => ({ ...prev, xlShipmentsAuthorised: event.target.checked }));
  }, []);

  const handleGeneTemplateSelectionChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setGeneTemplateForm(prev => ({
      selection: value,
      customName: value.startsWith('custom:') ? prev.customName : '',
    }));
    setTemplateSelectionError(null);
    setCustomTemplateNameError(null);
  }, []);

  const handleCustomTemplateNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setGeneTemplateForm(prev => ({ ...prev, customName: event.target.value }));
    setCustomTemplateNameError(null);
  }, []);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      if (props.mode === 'create') {
        createWorkbook.reset();
        setCreateFormState(() => createEmptyCreateFormState());
        setGeneTemplateForm(() => createEmptyGeneTemplateFormState());
        setTemplateSelectionError(null);
        setCustomTemplateNameError(null);
        setChannelUrlError(null);
        setLabSheetUrlError(null);
        setShippingRequestQuotaError(null);
        void refetchGenePanels();
        void refetchGenePanelAssays();
        setIsSubmittingWorkbook(false);
      } else {
        if (editInitialMetadata) {
          setMetadataFormState(() => deriveMetadataFormStateFromProps(editInitialMetadata));
        }
        setChannelUrlError(null);
        setIsSubmittingWorkbook(false);
      }
    }
    wasOpenRef.current = props.isOpen;
  }, [createWorkbook, editInitialMetadata, props.isOpen, props.mode, refetchGenePanelAssays, refetchGenePanels]);

  const handleCreateSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const { value: normalizedLabSheetUrl, isValid } = normalizeLabSheetUrlInputValue(createFormState.labSheetUrl);
      if (!isValid) {
        setLabSheetUrlError('Enter a valid legacy Google Sheets lab sheet URL.');
        return;
      }
      setLabSheetUrlError(null);

      const canAssignShippingQuota = props.mode === 'create' && props.canAssignShippingQuota;
      const shippingRequestQuota = canAssignShippingQuota ? Number(createFormState.shippingRequestQuota) : undefined;
      const xlShipmentsAuthorised = canAssignShippingQuota ? createFormState.xlShipmentsAuthorised : undefined;
      if (canAssignShippingQuota) {
        if (
          createFormState.shippingRequestQuota.trim() === '' ||
          !Number.isInteger(shippingRequestQuota) ||
          (shippingRequestQuota ?? -1) < 0 ||
          (shippingRequestQuota ?? 1001) > 1000
        ) {
          setShippingRequestQuotaError('Enter a whole number from 0 to 1000.');
          return;
        }
        setShippingRequestQuotaError(null);
      }

      const useLabSheetTemplate = Boolean(normalizedLabSheetUrl);
      const selectionDetails = useLabSheetTemplate ? null : parseTemplateSelection(geneTemplateForm.selection);
      if (!useLabSheetTemplate && !selectionDetails) {
        setTemplateSelectionError('Select an assay template option.');
        return;
      }

      const customName =
        !useLabSheetTemplate && selectionDetails?.type === 'custom'
          ? (() => {
              const validation = validateCustomTemplateName(geneTemplateForm.customName);
              if (!validation.valid) {
                return null;
              }
              return validation.trimmed;
            })()
          : undefined;
      if (customName === null) {
        return;
      }

      const channelUrlValidation = normalizeChannelUrlInputValue(createFormState.channelUrl);
      if (channelUrlValidation.error) {
        setChannelUrlError(channelUrlValidation.error);
        return;
      }
      setChannelUrlError(null);

      try {
        setIsSubmittingWorkbook(true);
        if (typeof organisationId !== 'number') {
          throw new Error('Organisation id missing');
        }

        const genePanelId =
          useLabSheetTemplate || !selectionDetails ? null : await ensurePanelForSelection(selectionDetails, customName);

        const body: Partial<CreateSampleSheetRequest> = {
          name: normalizeLabWorkbookInputValue(createFormState.name),
          projectLead: normalizeLabWorkbookInputValue(createFormState.projectLead),
          labSheetUrl: normalizedLabSheetUrl,
          channelUrl: channelUrlValidation.value,
          genePanelId,
          services: getServicesForChoiceValue(createFormState.servicesChoice),
          shippingRequestQuota,
          xlShipmentsAuthorised,
        };

        await createWorkbook.mutateAsync({ organisationId, body });
        props.onClose();
        if (props.mode === 'create') {
          props.onCreated?.();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create lab workbook';
        showToast({
          title: 'Failed to create lab workbook',
          description: message,
          status: 'error',
        });
      } finally {
        setIsSubmittingWorkbook(false);
      }
    },
    [
      createFormState.channelUrl,
      createFormState.labSheetUrl,
      createFormState.name,
      createFormState.projectLead,
      createFormState.servicesChoice,
      createFormState.shippingRequestQuota,
      createFormState.xlShipmentsAuthorised,
      ensurePanelForSelection,
      geneTemplateForm.customName,
      geneTemplateForm.selection,
      organisationId,
      props,
      createWorkbook,
      showToast,
      validateCustomTemplateName,
    ],
  );

  const handleEditSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (props.mode !== 'edit') {
        return;
      }
      const channelUrlValidation = normalizeChannelUrlInputValue(metadataFormState.channelUrl);
      if (channelUrlValidation.error) {
        setChannelUrlError(channelUrlValidation.error);
        return;
      }
      setChannelUrlError(null);
      try {
        const payload = {
          name: normalizeLabWorkbookInputValue(metadataFormState.name),
          projectLead: normalizeLabWorkbookInputValue(metadataFormState.projectLead),
          channelUrl: channelUrlValidation.value,
        };
        const summary = await updateMetadata.mutateAsync({ workbookId: props.workbookId, body: payload });
        queryClient.setQueryData(['labWorkbook', props.workbookId], summary);
        await queryClient.invalidateQueries({ queryKey: ['labWorkbook', props.workbookId] });
        toast({
          title: 'Workbook updated',
          description: 'Metadata saved.',
          status: 'success',
        });
        props.onClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Save failed. Please try again.';
        toast({
          title: 'Save failed',
          description: message,
          status: 'error',
        });
      }
    },
    [
      metadataFormState.channelUrl,
      metadataFormState.name,
      metadataFormState.projectLead,
      props,
      queryClient,
      toast,
      updateMetadata,
    ],
  );

  const isEditSubmitting = updateMetadata.isPending;
  const isEditMode = props.mode === 'edit';
  const submitLabel = isEditMode ? 'Save' : 'Create workbook';
  const headerLabel = isEditMode ? 'Edit lab workbook' : 'Create lab workbook';
  const onSubmit = isEditMode ? handleEditSubmit : handleCreateSubmit;

  const resetControl = useMemo(() => {
    if (props.mode !== 'edit') {
      return null;
    }
    return (
      <ResetLabWorkbookControl
        workbookId={props.workbookId}
        workbookName={props.initialMetadata.name}
        disabled={!props.resetEligibility.eligible}
        reasons={props.resetEligibility.reasons}
        onReset={() => {
          props.onReset?.();
          props.onClose();
        }}
      />
    );
  }, [props]);

  const deleteControl = useMemo(() => {
    if (props.mode !== 'edit') {
      return null;
    }
    return (
      <DeleteLabWorkbookControl
        workbookId={props.workbookId}
        workbookName={props.initialMetadata.name}
        disabled={!props.deleteEligibility.eligible}
        reasons={props.deleteEligibility.reasons}
        onDeleted={() => {
          props.onDeleted?.();
        }}
      />
    );
  }, [props]);

  const disableClose = isSubmittingWorkbook || createWorkbook.isPending || isEditSubmitting;

  return (
    <Modal isOpen={props.isOpen} onClose={handleCloseModal} isCentered>
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={event => {
          void onSubmit(event as FormEvent<HTMLFormElement>);
        }}
      >
        <ModalHeader>{headerLabel}</ModalHeader>
        <ModalCloseButton isDisabled={disableClose} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                autoFocus
                value={activeMetadataState.name}
                onChange={updateMetadataField('name')}
                placeholder="Workbook name"
                maxLength={200}
                isDisabled={disableClose}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Project lead</FormLabel>
              <Input
                value={activeMetadataState.projectLead}
                onChange={updateMetadataField('projectLead')}
                placeholder="Resistomap or partner lab project lead"
                maxLength={200}
                isDisabled={disableClose}
              />
            </FormControl>

            <FormControl isInvalid={Boolean(channelUrlError)}>
              <FormLabel>Channel URL</FormLabel>
              <Input
                type="url"
                value={activeMetadataState.channelUrl}
                onChange={handleChannelUrlChange}
                placeholder="https://..."
                maxLength={2048}
                isDisabled={disableClose}
              />
              {channelUrlError ? <FormErrorMessage>{channelUrlError}</FormErrorMessage> : null}
            </FormControl>

            {props.mode === 'create' ? (
              <>
                <FormControl isInvalid={Boolean(labSheetUrlError)}>
                  <FormLabel>Legacy lab sheet URL (historical imports only)</FormLabel>
                  <Input
                    type="url"
                    value={createFormState.labSheetUrl}
                    onChange={handleCreateLabSheetUrlChange}
                    placeholder="https://..."
                    isDisabled={disableClose}
                  />
                  {labSheetUrlError ? <FormErrorMessage>{labSheetUrlError}</FormErrorMessage> : null}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Services</FormLabel>
                  <Select
                    value={createFormState.servicesChoice}
                    onChange={handleCreateServicesChange}
                    isDisabled={disableClose}
                  >
                    {labWorkbookServiceChoices.map(choice => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {props.canAssignShippingQuota ? (
                  <VStack align="stretch" spacing={2}>
                    <FormControl isRequired isInvalid={Boolean(shippingRequestQuotaError)}>
                      <FormLabel>Available shipping requests</FormLabel>
                      <Input
                        type="number"
                        min={0}
                        max={1000}
                        step={1}
                        value={createFormState.shippingRequestQuota}
                        onChange={handleShippingRequestQuotaChange}
                        isDisabled={disableClose}
                      />
                      {shippingRequestQuotaError ? (
                        <FormErrorMessage>{shippingRequestQuotaError}</FormErrorMessage>
                      ) : (
                        <FormHelperText>
                          Number of shipment pickup requests this workbook can use immediately.
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Checkbox
                      isChecked={createFormState.xlShipmentsAuthorised}
                      onChange={handleXlShipmentsAuthorisedChange}
                      isDisabled={disableClose}
                    >
                      XL shipments authorised
                    </Checkbox>
                  </VStack>
                ) : null}

                <FormControl
                  isRequired={!usesLabSheetTemplate}
                  isInvalid={!usesLabSheetTemplate && Boolean(templateSelectionError)}
                >
                  <FormLabel>Assay template</FormLabel>
                  <Select
                    value={selectionInputValue}
                    onChange={handleGeneTemplateSelectionChange}
                    isDisabled={isTemplateSelectionDisabled}
                  >
                    {templateSelectOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {!usesLabSheetTemplate && templateSelectionError ? (
                    <FormErrorMessage>{templateSelectionError}</FormErrorMessage>
                  ) : null}
                </FormControl>

                {isTemplateLoading ? (
                  <HStack spacing={2} color="gray.600">
                    <Spinner size="sm" />
                    <Text fontSize="sm">Loading assay template options…</Text>
                  </HStack>
                ) : null}

                {templateSummaryMessage ? (
                  <Text fontSize="sm" color="gray.600">
                    {templateSummaryMessage}
                  </Text>
                ) : null}

                {isCustomSelection ? (
                  <FormControl isRequired isInvalid={Boolean(customTemplateNameError)}>
                    <FormLabel>Custom template name</FormLabel>
                    <Input
                      value={geneTemplateForm.customName}
                      onChange={handleCustomTemplateNameChange}
                      onBlur={handleCustomTemplateNameBlur}
                      placeholder="e.g. Custom 36 genes"
                      maxLength={200}
                      isDisabled={disableClose}
                    />
                    {customTemplateNameError ? (
                      <FormErrorMessage>{customTemplateNameError}</FormErrorMessage>
                    ) : (
                      <FormHelperText>Provide a descriptive, unique name for this custom configuration.</FormHelperText>
                    )}
                  </FormControl>
                ) : null}

                {defaultDefinitionError ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>{defaultDefinitionError.message}</AlertDescription>
                  </Alert>
                ) : null}

                {genePanelErrorMessage ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      Failed to load existing gene panels. You can still commission default or custom templates. (
                      {genePanelErrorMessage})
                    </AlertDescription>
                  </Alert>
                ) : null}

                {genePanelAssaysErrorMessage ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      Default template definitions are unavailable right now. Please use existing or custom templates. (
                      {genePanelAssaysErrorMessage})
                    </AlertDescription>
                  </Alert>
                ) : null}
              </>
            ) : null}

            {isEditMode ? (
              <>
                <Divider />
                {props.canManageShipping ? (
                  <>
                    <LabWorkbookShippingSettingsControl
                      workbookId={props.workbookId}
                      organisationId={props.organisationId}
                      isEnabled={props.isOpen}
                    />
                    <Divider />
                  </>
                ) : null}
                <Box>
                  <Text fontWeight={600} mb={2} color={theme.colors.neutral800}>
                    Admin actions
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {resetControl}
                    {deleteControl}
                  </VStack>
                </Box>
              </>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={handleCloseModal} type="button" isDisabled={disableClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={disableClose}
              loadingText={isEditMode ? 'Saving' : 'Creating'}
            >
              {submitLabel}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
